import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MessageSquare, Send, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';
import './StaticPages.css';

export default function Contact() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate form submission
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      // Reset success message after 5 seconds
      setTimeout(() => setSubmitted(false), 5000);
    }, 1000);
  };

  return (
    <div className="static-page">
      <SEO 
        title="Contact Us - RazChatz | Get Support & Help"
        description="Contact RazChatz support team. Get help, report issues, or send feedback. We respond within 24 hours. Email: support@razchatz.com"
        keywords="contact razchatz, customer support, help center, technical support, contact form, get help, razchatz support, feedback"
        canonical="https://razchatz.netlify.app/contact"
        ogTitle="Contact RazChatz - We're Here to Help"
        ogDescription="Need help or have questions? Contact RazChatz support team. We provide 24/7 assistance for all users."
      />
      <div className="static-page-header">
        <button className="back-button" onClick={() => navigate('/')}>
          <ArrowLeft size={20} />
          Back to Home
        </button>
      </div>

      <div className="static-page-container">
        <h1 className="static-page-title">Contact Us</h1>
        
        <div className="static-page-content">
          <section className="contact-intro">
            <p className="lead-text">
              Have questions, feedback, or need support? We'd love to hear from you! Our team is 
              here to help and typically responds within 24 hours.
            </p>
          </section>

          <div className="contact-grid">
            <div className="contact-info-section">
              <h2>Get in Touch</h2>
              
              <div className="contact-method">
                <div className="contact-icon-wrapper">
                  <Mail className="contact-icon" />
                </div>
                <div>
                  <h3>Email Support</h3>
                  <p>For general inquiries and support</p>
                  <a href="mailto:support@razchatz.com" className="contact-link">
                    support@razchatz.com
                  </a>
                </div>
              </div>

              <div className="contact-method">
                <div className="contact-icon-wrapper">
                  <MessageSquare className="contact-icon" />
                </div>
                <div>
                  <h3>Business Inquiries</h3>
                  <p>For partnerships and business matters</p>
                  <a href="mailto:business@razchatz.com" className="contact-link">
                    business@razchatz.com
                  </a>
                </div>
              </div>

              <div className="contact-method">
                <div className="contact-icon-wrapper">
                  <Mail className="contact-icon" />
                </div>
                <div>
                  <h3>Technical Support</h3>
                  <p>For technical issues and bugs</p>
                  <a href="mailto:tech@razchatz.com" className="contact-link">
                    tech@razchatz.com
                  </a>
                </div>
              </div>

              <div className="contact-hours">
                <h3>Support Hours</h3>
                <p>We provide 24/7 support for critical issues.</p>
                <p>General inquiries: Monday - Friday, 9 AM - 6 PM (IST)</p>
              </div>
            </div>

            <div className="contact-form-section">
              <h2>Send Us a Message</h2>
              
              {submitted && (
                <div className="success-message">
                  <CheckCircle size={20} />
                  <span>Thank you! Your message has been sent successfully.</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name">Your Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject *</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="support">Technical Support</option>
                    <option value="feedback">Feedback</option>
                    <option value="bug">Report a Bug</option>
                    <option value="feature">Feature Request</option>
                    <option value="business">Business Inquiry</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Tell us how we can help you..."
                  />
                </div>

                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send size={18} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          <section className="faq-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-grid">
              <div className="faq-item">
                <h3>How do I reset my password?</h3>
                <p>
                  Click on "Forgot Password" on the login page and follow the instructions sent 
                  to your email.
                </p>
              </div>

              <div className="faq-item">
                <h3>Is RazChatz free to use?</h3>
                <p>
                  Yes! RazChatz is completely free to use with all core features available to 
                  all users.
                </p>
              </div>

              <div className="faq-item">
                <h3>How do I delete my account?</h3>
                <p>
                  Go to Settings → Account → Delete Account. Note that this action is permanent 
                  and cannot be undone.
                </p>
              </div>

              <div className="faq-item">
                <h3>Can I use RazChatz on mobile?</h3>
                <p>
                  Absolutely! RazChatz is fully optimized for mobile devices and works seamlessly 
                  on smartphones and tablets.
                </p>
              </div>

              <div className="faq-item">
                <h3>How secure is RazChatz?</h3>
                <p>
                  We use end-to-end encryption for all messages and implement industry-standard 
                  security measures to protect your data.
                </p>
              </div>

              <div className="faq-item">
                <h3>How do I report inappropriate content?</h3>
                <p>
                  Contact us immediately at support@razchatz.com with details. We take all reports 
                  seriously and act quickly.
                </p>
              </div>
            </div>
          </section>

          <section className="cta-section">
            <h2>Ready to Start Chatting?</h2>
            <p>
              Join thousands of users already enjoying RazChatz's seamless messaging experience.
            </p>
            <div className="cta-buttons">
              <button className="cta-button primary" onClick={() => navigate('/signup')}>
                Create Account
              </button>
              <button className="cta-button secondary" onClick={() => navigate('/about')}>
                Learn More
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
